import { useEffect, useState, useRef } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * GoogleTagManager — Loads GTM on public pages only.
 * Reads config from backend (gtm_id + active flag).
 * Injects <script> and <noscript> tags, pushes page_view on route change.
 */
export default function GoogleTagManager() {
  const [gtmId, setGtmId] = useState("");
  const injected = useRef(false);
  const location = useLocation();

  // Fetch GTM config once
  useEffect(() => {
    axios.get(`${API}/api/gtm/config`).then(r => {
      if (r.data?.active && r.data?.gtm_id) {
        setGtmId(r.data.gtm_id);
      }
    }).catch(() => {});
  }, []);

  // Inject GTM script when gtmId becomes available
  useEffect(() => {
    if (!gtmId || injected.current) return;
    injected.current = true;

    // Initialize dataLayer
    window.dataLayer = window.dataLayer || [];

    // GTM head script
    const script = document.createElement("script");
    script.innerHTML = `
      (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
      new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
      j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
      'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
      })(window,document,'script','dataLayer','${gtmId}');
    `;
    document.head.appendChild(script);

    // GTM noscript iframe
    const noscript = document.createElement("noscript");
    const iframe = document.createElement("iframe");
    iframe.src = `https://www.googletagmanager.com/ns.html?id=${gtmId}`;
    iframe.height = "0";
    iframe.width = "0";
    iframe.style.display = "none";
    iframe.style.visibility = "hidden";
    noscript.appendChild(iframe);
    document.body.insertBefore(noscript, document.body.firstChild);
  }, [gtmId]);

  // Push virtual page_view on route change
  useEffect(() => {
    if (!gtmId) return;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "page_view",
      page_path: location.pathname,
      page_title: document.title,
      page_location: window.location.href,
    });
  }, [location.pathname, gtmId]);

  return null; // no visible output
}

/**
 * Push a structured event to GTM dataLayer.
 * Use this from any public page for custom tracking.
 */
export function pushEvent(eventName, params = {}) {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: eventName,
    ...params,
  });
}
